import Vue from "vue";
import { done } from "../customEvents/event";

/**
 * Mutations for chat
 */
export const mutations = {
  /**
   * Start message loading
   *
   * @param {Object} state
   * @param {ID} id
   */
  startLoadingMessage(state, id) {
    Vue.set(state.loadingMessage, id, true);
  },

  /**
   * Set the value of the current selected chat user
   *
   * @param {*} state
   * @param {String} value
   */
  setCurrentSelectedNew(state, value) {
    state.currentSelectedNew = value;
  },

  /**
   * Start group message loading
   *
   * @param {Object} state
   * @param {ID} id
   */
  startLoadingGroupMessage(state, id) {
    Vue.set(state.loadingGroupMessage, id, true);
  },

  /**
   * Stop loading message
   *
   * @param {Object} state
   * @param {Id} id
   */
  stopLoadingMessage(state, id) {
    Vue.set(state.loadingMessage, id, false);
  },

  /**
   * Stop loading group message
   *
   * @param {Object} state
   * @param {Id} id
   */
  stopLoadingGroupMessage(state, id) {
    Vue.set(state.loadingGroupMessage, id, false);
  },

  /**
   * Set the is group
   *
   * @param {*} state
   * @param {Boolean} value
   */
  setIsGroup(state, value) {
    state.isGroup = value;
  },

  /**
   * Start sending message
   *
   * @param {Object} state
   * @param {Id} id
   */
  startSendingMessage(state, id) {
    if (id in state.sendingMessage) {
      Vue.set(state.sendingMessage, id, state.sendingMessage[id] + 1);
    } else {
      Vue.set(state.sendingMessage, id, 1);
    }
  },

  /**
   * Stop sending message
   *
   * @param {Object} state
   * @param {Id} id
   */
  stopSendingMessage(state, id) {
    Vue.set(state.sendingMessage, id, state.sendingMessage[id] - 1);
    if (state.sendingMessage[id] === 0) {
      window.dispatchEvent(done);
    }
  },

  /**
   * Start sending message
   *
   * @param {Object} state
   * @param {Id} id
   */
  startSendingGroupMessage(state, id) {
    if (id in state.sendingGroupMessage) {
      Vue.set(state.sendingGroupMessage, id, state.sendingGroupMessage[id] + 1);
    } else {
      Vue.set(state.sendingGroupMessage, id, 1);
    }
  },

  /**
   * Stop sending message
   *
   * @param {Object} state
   * @param {Id} id
   */
  stopSendingGroupMessage(state, id) {
    Vue.set(state.sendingGroupMessage, id, state.sendingGroupMessage[id] - 1);
    if (state.sendingGroupMessage[id] === 0) {
      window.dispatchEvent(done);
    }
  },

  /**
   * Set the selected user id to show their message and send message to them
   *
   * @param {Object} state
   * @param {Number} value
   */
  setSelectedUserId(state, value) {
    state.selectedUserId = parseInt(value);
  },

  /**
   * Set the friends
   *
   * @param {Object} state
   * @param {Object} param1
   */
  setFriends(state, { unread, details, groupDetails, primaryId, unreadGroup }) {
    // Step 1: Create an unread message object
    const unreadMessageObject = {};
    const groupUnreadMessageObject = {};

    // Step 2: Loop through all the unread messages and make an unread message object
    unread.forEach(unreadMessage => {
      unreadMessageObject[unreadMessage.id] = unreadMessage.unread_messages;
    });
    unreadGroup.forEach(unreadMessage => {
      groupUnreadMessageObject[unreadMessage.id] =
        unreadMessage.unread_messages;
    });

    const newFriends = {};
    details.forEach(detail => {
      let id = detail.sent_by;
      if (id === primaryId) {
        id = detail.receiver_id;
      }

      if (id in newFriends) {
        if (newFriends[id].message_id < detail.message_id) {
          newFriends[id] = detail;
        }
      } else {
        newFriends[id] = detail;
      }

      if (id in unreadMessageObject) {
        newFriends[id]["unread"] = unreadMessageObject[id];
      } else {
        newFriends[id]["unread"] = 0;
      }
    });

    // Step 3: Set the unread message object
    state.friends = newFriends;

    // Step 4: Loop through all the groupDetails and set the values
    const newGroups = {};
    groupDetails.forEach(detail => {
      const id = detail.receiver_id;

      if (id in newGroups) {
        if (newGroups[id].message_id < detail.message_id) {
          newGroups[id] = detail;
        }
      } else {
        newGroups[id] = detail;
      }

      if (id in groupUnreadMessageObject) {
        newGroups[id]["unread"] = groupUnreadMessageObject[id];
      } else {
        newGroups[id]["unread"] = 0;
      }
    });

    // Step 5: Set the groups
    state.groups = newGroups;
  },

  /**
   * Update the client details after edit
   *
   * @param {Object} state
   * @param {Object} param1
   */
  updateNewClient(state, { value, clientId }) {
    // Step 1: Check if the chat for that client exists
    if (!state.friends || !(clientId in state.friends)) {
      return;
    }

    // Step 2: Update the friend
    Vue.set(state.friends, clientId, { ...state.friends[clientId], ...value });
  },

  /**
   * Setting the unread as 0 when message has been read
   *
   * @param {Object} state
   * @param {Number} id
   */
  removeUnread(state, { id, isGroup }) {
    const friends = isGroup ? "groups" : "friends";
    const messages = isGroup ? "groupMessage" : "messages";
    if (state[friends] && id in state[friends]) {
      const newFriend = { ...state[friends][id] };
      newFriend.unread = 0;
      Vue.set(state[friends], id, newFriend);
      const newFrendsMessages = state[messages][id].map(message => {
        if (message.sent_by === id) {
          message.read_time = Date.now();
        }
        return message;
      });
      Vue.set(state[messages], id, newFrendsMessages);
    }
  },

  /**
   * Setting first name, middle name, last name, name prefix and name suffix
   * of the main secondary chat user
   * For portal users, their subscriber is the main secondary chat user
   *
   * @param {Object} state
   * @param {Object} param1
   */
  setName(state, { firstName, middleName, lastName, namePrefix, nameSuffix }) {
    state.firstName = firstName;
    state.middleName = middleName;
    state.lastName = lastName;
    state.namePrefix = namePrefix;
    state.nameSuffix = nameSuffix;
  },

  /**
   * Setting the virtual page for the message
   *
   * @param {Object} state
   * @param {OES_element_index_uint} param1
   */
  setPage(state, { providerId, userId, pageNumber, isGroup = false }) {
    // Step 1: Inner separation
    let inner = "user";
    if (isGroup) {
      inner = "group";
    }

    // Step 2: Set the value
    Vue.set(state.pages, providerId, {
      ...(state.pages[providerId] || {}),
      [inner]: state.pages[providerId]
        ? {
            ...(state.pages[providerId][inner] || {}),
            [userId]: pageNumber
          }
        : {
            [userId]: pageNumber
          }
    });
  },

  /**
   * Weather the last page is reached or all messages are loaded
   * If all messages are loaded then no need to load any more messages
   *
   * @param {Object} state
   * @param {Object} param1
   */
  setLastPage(state, { userId, providerId, isGroup = true }) {
    // Step 1: Inner separation
    let inner = "user";
    if (isGroup) {
      inner = "group";
    }

    // Step 2: Set the value
    Vue.set(state.lastPage, providerId, {
      ...(state.lastPage[providerId] || {}),
      [inner]: state.lastPage[providerId]
        ? {
            ...(state.lastPage[providerId][inner] || {}),
            [userId]: true
          }
        : {
            [userId]: true
          }
    });
  },

  /**
   * Add message to the begining
   *
   * @param {Object} state
   * @param {Object} param1
   */
  prependMessages(state, { messages, id, isGroup }) {
    if (isGroup) {
      Vue.set(state.groupMessage, id, [
        ...messages,
        ...(state.groupMessage[id] || [])
      ]);
    } else {
      Vue.set(state.messages, id, [...messages, ...(state.messages[id] || [])]);
    }
  },

  /**
   * Append new remaining messages to the bottom
   *
   * @param {Object} state
   * @param {Object} param1
   */
  appendRemainingMessages(state, { messages, id, isGroup }) {
    // Step 1: Unique message id to prevent duplication of message
    const ids = {};

    // Step 2: Reverse the messages
    messages.reverse();

    // Step 2: Create the new message array
    const newMessages = [...state.messages[id], ...messages].filter(message => {
      if (message.id in ids && ids[message.id]) {
        return false;
      } else {
        ids[message.id] = true;
        return true;
      }
    });

    // Step 3: Set the state
    Vue.set(state.messages, id, newMessages);

    // Step 4: Setting new offset
    let inner = isGroup ? "group" : "user";
    const newOffset = {
      ...(state.additionalOffset[inner] || {}),
      [id]: state.additionalOffset[inner]
        ? state.additionalOffset[inner][id] || 0 + messages.length
        : messages.length
    };
    Vue.set(state.additionalOffset, inner, newOffset);
  },

  /**
   * Add message from the client
   * Sending message
   *
   * @param {Object} state
   * @param {Object} param1
   */
  addMessageFromClient(state, { id, message, isGroup }) {
    const messages = state[`${isGroup ? "groupMessage" : "messages"}`];
    Vue.set(messages, id, [...(messages[id] || []), message]);

    // Increase the additional page offset
    let inner = isGroup ? "group" : "user";
    const newOffset = {
      ...(state.additionalOffset[inner] || {}),
      [id]: state.additionalOffset[inner]
        ? state.additionalOffset[inner][id] || 0 + 1
        : 1
    };
    Vue.set(state.additionalOffset, inner, newOffset);
  },

  /**
   * Update the added message details
   *
   * @param {Object} state
   * @param {Object} param1
   */
  updateAddedMessage(state, { messageId, index, friendId, isGroup }) {
    const messages = state[`${isGroup ? "groupMessage" : "messages"}`];
    messages[friendId][index].id = messageId;
    Vue.set(messages, friendId, messages[friendId]);
  },

  /**
   * Add message from the server
   *
   * @param {Object} state
   * @param {Object} param1
   */
  addMessageFromServer(state, { id, message, isGroup }) {
    const messages = state[`${isGroup ? "groupMessage" : "messages"}`];
    Vue.set(messages, id, [...(messages[id] || []), message]);

    // Increase the additional page offset
    let inner = isGroup ? "group" : "user";
    const newOffset = {
      ...(state.additionalOffset[inner] || {}),
      [id]: state.additionalOffset[inner]
        ? state.additionalOffset[inner][id] || 0 + 1
        : 1
    };
    Vue.set(state.additionalOffset, inner, newOffset);
  },

  /**
   * Update details about friend
   *
   * @param {Object} state
   * @param {Object} param1
   */
  updateFriendDetails(state, { id, message, isGroup, groupUsername }) {
    const friends = isGroup ? "groups" : "friends";
    if (state[friends] && id in state[friends]) {
      const newFriend = { ...state[friends][id] };
      newFriend.message_id = message.id;
      newFriend.value = message.value;
      newFriend.time = message.sent_time;
      newFriend.group_name = state[friends][id].group_name;
      newFriend.userRole = state.currentSelectedNew;
      if (id === message.sent_by) {
        newFriend.unread = newFriend.unread + 1;
      }
      Vue.set(state[friends], id, newFriend);
    } else if (state[friends]) {
      // Add new friend if friend doesn't exist
      const newFriend = { ...message };
      newFriend.unread = 1;
      if (!("online" in newFriend)) {
        newFriend.online = true;
      }
      newFriend.message_id = message.id;
      newFriend.time = message.sent_time;
      newFriend.group_name = groupUsername;
      newFriend.userRole = state.currentSelectedNew;
      Vue.set(state[friends], id, newFriend);
    }
  },

  /**
   * Set the the specified friend is online
   *
   * @param {Object} state
   * @param {Number} id
   */
  friendOnline(state, id) {
    if (state.friends && id in state.friends) {
      const newFriend = { ...state.friends[id] };
      newFriend.online = true;
      Vue.set(state.friends, id, newFriend);
    }
  },

  /**
   * Set that the specified friend is offline
   *
   * @param {Object} state
   * @param {Number} id
   */
  friendOffline(state, id) {
    if (state.friends && id in state.friends) {
      const newFriend = { ...state.friends[id] };
      newFriend.online = null;
      Vue.set(state.friends, id, newFriend);
    }
  },

  /**
   * Add the loaded message of the user
   *
   * @param {Object} state
   * @param {Object} param1
   */
  addLoadedMessagesOfUsers(state, { userId, providerId }) {
    if (
      providerId in state.loadedMessagesOfUsers &&
      state.loadedMessagesOfUsers[providerId].indexOf(userId) === -1
    ) {
      Vue.set(state.loadedMessagesOfUsers, providerId, [
        ...state.loadedMessagesOfUsers[providerId],
        userId
      ]);
    } else if (!(providerId in state.loadedMessagesOfUsers)) {
      Vue.set(state.loadedMessagesOfUsers, providerId, [userId]);
    }
  },

  /**
   * Add or remove the favourite id of a friend
   *
   * @param {Object} state
   * @param {Object} param1
   */
  addOrRemoveFavourites(state, { user, res, addOrRemove, isGroup }) {
    const friends = isGroup ? "groups" : "friends";

    // Step 1: Check if friends have been fetched
    if (!state[friends]) {
      return false;
    }

    // Step 2: If friend is not there return false
    if (!(user.id in state[friends])) {
      return false;
    }

    // Step 3: Update friend
    const curFriend = { ...state[friends][user.id] };
    if (addOrRemove === "ADD") {
      curFriend.favourite_id = res.favourite.id;
    } else {
      curFriend.favourite_id = null;
    }

    // Step 4: Set the friend
    Vue.set(state[friends], user.id, curFriend);
  },

  /**
   * Remove loaded message of the user
   *
   * @param {Object} state
   * @param {Object} param1
   */
  removeLoadedMessagesOfUsers(state, { userId, providerId }) {
    const index = state.loadedMessagesOfUsers[providerId]
      ? state.loadedMessagesOfUsers[providerId].indexOf(userId)
      : -1;
    if (index !== -1) {
      const newLoaded = [...state.loadedMessagesOfUsers[providerId]];
      newLoaded.splice(index, 1);
      Vue.set(state.loadedMessagesOfUsers, providerId, newLoaded);
    }
  },

  /**
   * Add the loaded message of the group
   *
   * @param {Object} state
   * @param {Object} param1
   */
  addLoadedMessagesOfGroups(state, { userId, providerId }) {
    if (
      providerId in state.loadedMessagesOfGroups &&
      state.loadedMessagesOfGroups[providerId].indexOf(userId) === -1
    ) {
      Vue.set(state.loadedMessagesOfGroups, providerId, [
        ...state.loadedMessagesOfGroups[providerId],
        userId
      ]);
    } else if (!(providerId in state.loadedMessagesOfGroups)) {
      Vue.set(state.loadedMessagesOfGroups, providerId, [userId]);
    }
  },

  /**
   * Remove loaded message of the group
   *
   * @param {Object} state
   * @param {Object} param1
   */
  removeLoadedMessagesOfGroups(state, { userId, providerId }) {
    const index = state.loadedMessagesOfGroups[providerId]
      ? state.loadedMessagesOfGroups[providerId].indexOf(userId)
      : -1;
    if (index !== -1) {
      const newLoaded = [...state.loadedMessagesOfGroups[providerId]];
      newLoaded.splice(index, 1);
      Vue.set(state.loadedMessagesOfGroups, providerId, newLoaded);
    }
  },

  /**
   * Clear state when logging out
   * @param {Object} state
   */
  clear(state) {
    state.isGroup = false;
    state.messages = {};
    state.groupMessage = {};
    state.pages = {};
    state.additionalOffset = {};
    state.lastPage = {};
    state.loadingMessage = {};
    state.sendingMessage = {};
    state.loadedMessagesOfUsers = {};
    state.loadedMessagesOfGroups = {};
    state.firstName = "";
    state.middleName = "";
    state.lastName = "";
    state.namePrefix = "";
    state.nameSuffix = "";
    state.friends = false;
    state.groups = false;
    state.currentSelectedNew = "";
  }
};
